import type { ReactNode } from 'react'
import type { IntlShape } from 'react-intl'

import { createRoutes } from '../../../routes'

export const STEP_DURATION_MS = 7000

// Step identity and order; the stage keys its scenes off these ids.
export const AGENT_STEP_IDS = [
  'ask',
  'search',
  'explain',
  'draft',
  'approve',
] as const

export type AgentStepId = (typeof AGENT_STEP_IDS)[number]

export type AgentStep = {
  id: AgentStepId
  /** Bold lead of the title, e.g. "Ask" in "Ask any question". */
  titleLead: string
  titleRest: string
  description: ReactNode
  /** Step accent (same palette as the solutions pages). */
  color: string
  /** Lighter tint of the accent for large surfaces like the stage circle. */
  surfaceColor: string
}

export function getAgentSteps(intl: IntlShape): AgentStep[] {
  const routes = createRoutes(intl.locale)
  return [
    {
      id: 'ask',
      surfaceColor: 'var(--slite-blue-300)',
      color: 'var(--slite-blue-500)',
      titleLead: intl.formatMessage({
        id: 'agent.howItWorks.ask.titleLead',
        defaultMessage: 'Ask',
      }),
      titleRest: intl.formatMessage({
        id: 'agent.howItWorks.ask.titleRest',
        defaultMessage: 'any question',
      }),
      description: intl.formatMessage({
        id: 'agent.howItWorks.ask.desc',
        defaultMessage:
          'Start with a question, task or change in the business. You don’t need to know where the answer lives or which documents might be affected.',
      }),
    },
    {
      id: 'search',
      surfaceColor: 'var(--slite-orange-300)',
      color: 'var(--slite-orange-400)',
      titleLead: intl.formatMessage({
        id: 'agent.howItWorks.search.titleLead',
        defaultMessage: 'Search',
      }),
      titleRest: intl.formatMessage({
        id: 'agent.howItWorks.search.titleRest',
        defaultMessage: 'every relevant source',
      }),
      description: intl.formatMessage(
        {
          id: 'agent.howItWorks.search.desc',
          defaultMessage:
            'Slite Agent runs <link>enterprise AI search</link> across Slite, Slack, Linear, GitHub and your other connected sources while respecting existing permissions. It brings the most relevant cited answers together in one place.',
        },
        {
          link: (chunks) => (
            <a
              href={routes.aiSearch()}
              className="shade-00 font-weight-bold underline-on-hover"
            >
              {chunks}
            </a>
          ),
        }
      ),
    },
    {
      id: 'explain',
      surfaceColor: 'var(--slite-yellow-300)',
      color: 'var(--slite-yellow-500)',
      titleLead: intl.formatMessage({
        id: 'agent.howItWorks.explain.titleLead',
        defaultMessage: 'Explain',
      }),
      titleRest: intl.formatMessage({
        id: 'agent.howItWorks.explain.titleRest',
        defaultMessage: 'the answer with sources',
      }),
      description: intl.formatMessage({
        id: 'agent.howItWorks.explain.desc',
        defaultMessage:
          'Slite Agent compares the evidence and explains the current answer in plain language. Every claim remains connected to its source, so your team can check the reasoning.',
      }),
    },
    {
      id: 'draft',
      surfaceColor: 'var(--slite-pink-300)',
      color: 'var(--slite-pink-500)',
      titleLead: intl.formatMessage({
        id: 'agent.howItWorks.draft.titleLead',
        defaultMessage: 'Draft and automate',
      }),
      titleRest: intl.formatMessage({
        id: 'agent.howItWorks.draft.titleRest',
        defaultMessage: 'doc updates',
      }),
      description: intl.formatMessage(
        {
          id: 'agent.howItWorks.draft.desc',
          defaultMessage:
            'Slite Agent identifies the pages affected by what it found and <link>drafts focused changes</link>. If new documentation is needed, it creates a first draft from the same source material.',
        },
        {
          link: (chunks) => (
            <a
              href={routes.solutions.detail({
                slug: 'self-maintaining-documentation',
              })}
              className="shade-00 font-weight-bold underline-on-hover"
            >
              {chunks}
            </a>
          ),
        }
      ),
    },
    {
      id: 'approve',
      surfaceColor: 'var(--moon-dust-darkest)',
      color: 'var(--moon-dust-darkest)',
      titleLead: intl.formatMessage({
        id: 'agent.howItWorks.approve.titleLead',
        defaultMessage: 'Approve',
      }),
      titleRest: intl.formatMessage({
        id: 'agent.howItWorks.approve.titleRest',
        defaultMessage: 'every change',
      }),
      description: intl.formatMessage({
        id: 'agent.howItWorks.approve.desc',
        defaultMessage:
          'The document owner reviews the proposal and its supporting sources. Once approved, it becomes part of a verified knowledge base that every employee and AI agent can rely on.',
      }),
    },
  ]
}
